import { z } from 'zod/v4';

import { BACKGROUND_LAYER_SEEDS, createBorderSide } from '@sites/entity-core';

import { LINE_STYLES, SCROLL_EFFECTS } from '../host-enums';

import { THEME_COLOR_NAMES } from '../theme-schemas/update-theme-colors.schema';

/**
 * The bridge between the FLAT keys a model sends and the NESTED, per-breakpoint `props` a v0
 * block/column actually stores.
 *
 * v0 replaced legacy's four loose bags (`data`/`styles` plus a mobile and a tablet copy of each,
 * thirty-odd flat keys) with one tree whose leaves carry their own `{ desktop, tablet, mobile }`.
 * That is a better entity and a worse tool schema: a model asked for `background.color.desktop`
 * sends `backgroundColor` anyway, so the AI surface keeps legacy's flat vocabulary and this module
 * is where it lands on the tree. Shared by the block and the column folds, and through them by both
 * consumers — `apps/mcp-backend`'s mutations and `apps/mcp-backend`'s mutations — for the
 * same reason the widget folds are shared: the one time a merge was re-declared per consumer the
 * copies drifted apart.
 *
 * **Every write goes to all three breakpoints.** The flat surface has no way to say "tablet only",
 * the entities are seeded that way (`everywhere()` in `blocks.models.ts` and the entity-core
 * defaults), and `syncWithDesktop` defaults to `true` — so a model that sets a padding means the
 * padding, not the desktop padding. Writing desktop alone would leave the other two showing the old
 * value with nothing in the tool result hinting why.
 */

/** The v0 responsive leaf. `tablet`/`mobile` are optional — a reader falls back to `desktop`. */
export type Responsive<T> = { desktop: T; tablet?: T; mobile?: T };

/** A `props` tree, read dynamically: the three subtypes disagree about which keys exist. */
export type PropsBag = Record<string, unknown>;

const DEVICES = ['desktop', 'tablet', 'mobile'] as const;

/**
 * Seeds all three breakpoints from a FACTORY, never from a value: three keys pointing at one object
 * is a shared mutable leaf, and the next per-device edit would move all three.
 */
export const everyDevice = <T>(make: () => T): Responsive<T> => ({
    desktop: make(),
    tablet: make(),
    mobile: make(),
});

/**
 * Rewrites every breakpoint of one responsive leaf through `patch`, taking each device's CURRENT
 * value as the base (falling back to desktop, then to `seed()`), so a patch that only names
 * `paddingTop` keeps whatever the other three sides held on that device.
 */
export function patchEveryDevice<T>(current: unknown, patch: (value: T) => T, seed: () => T): Responsive<T> {
    const responsive = (current ?? {}) as Partial<Responsive<T>>;
    const at = (device: (typeof DEVICES)[number]): T => responsive[device] ?? responsive.desktop ?? seed();

    return { desktop: patch(at('desktop')), tablet: patch(at('tablet')), mobile: patch(at('mobile')) };
}

const asBag = (value: unknown): PropsBag => (value && typeof value === 'object' ? { ...(value as PropsBag) } : {});

/** Drops `undefined` entries so an absent tool argument never overwrites a stored value with one. */
const definedEntries = <T extends object>(patch: T): Partial<T> =>
    Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined)) as Partial<T>;

/** True when the patch mentions at least one of `keys` — what decides whether a group is rebuilt. */
export const mentions = (patch: object, keys: readonly string[]): boolean =>
    keys.some((key) => (patch as PropsBag)[key] !== undefined);

// ---------------------------------------------------------------------------
// The flat groups a block and a column share, as schema shapes + their folds.
// ---------------------------------------------------------------------------

const BOX_PADDING_KEYS = ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'] as const;

export const BORDER_SIDES = ['Top', 'Right', 'Bottom', 'Left'] as const;

/** `borderWidth` & co. — the shorthand that writes all four sides at once. */
const BORDER_SHORTHAND_KEYS = ['borderWidth', 'borderStyle', 'borderColor'] as const;

const BORDER_SIDE_KEYS = BORDER_SIDES.flatMap((side) => [
    `border${side}Width`,
    `border${side}Style`,
    `border${side}Color`,
]);

const OVERLAY_KEYS = ['isOverlay', 'overlayColor', 'overlayOpacity'] as const;

/**
 * What a colour field accepts — and it is NOT the same answer for every colour field.
 *
 * A background resolves through `colorToken` → the host's `colorCss`, which turns a known palette
 * name into `var(--primary-color)` and passes anything else through as a literal. So `"primary"`
 * there is a live reference: the block follows the site when the palette changes, and one
 * `update_theme_colors` call restyles it along with everything else.
 *
 * A CONTOUR colour does not. The renderer builds the whole shorthand by hand —
 * `${width}px ${style} ${color}` (`block.css-vars.ts`, `column.css-vars.ts`) — with no resolver in
 * the way, so `"primary"` reaches the browser as `border: 1px solid primary`, which is invalid CSS
 * and therefore draws nothing. The editor agrees: its border picker destructures `{ value }` and
 * throws the picked `presetName` away (`sp-advanced-border-settings.component.ts`), so a contour
 * has never followed the theme. The same goes for the overlay tint, which is fed to
 * `toRgbaColor()` and has to be a colour it can parse.
 *
 * Both facts are stated in the schema rather than left to the prompt, because the failure they
 * prevent is silent: a caller that learns "theme names work" from `backgroundColor` will try one on
 * `borderColor`, get a 200, and report a contour that is not on the page.
 */
const FOLLOWS_THEME =
    ` Accepts a THEME COLOUR NAME (${THEME_COLOR_NAMES.join(', ')}) or a CSS colour like "#1D4ED8".` +
    ' Prefer the name — it follows the site when the palette changes. get_page_snapshot reports what' +
    ' each name currently resolves to.';

const LITERAL_ONLY = ' Must be a CSS colour — a theme colour name does NOT work here.';

/**
 * The "follows the theme" clause, exported for the colour fields that live outside the shared box —
 * a block's shape dividers. Their fill goes through `blockShapeDividerColor`, which carries its own
 * copy of the five names, so a name works there exactly as it does on a background.
 */
export const THEME_COLOR_HINT = FOLLOWS_THEME;
const FILTER_KEYS = ['isFilterOverlay', 'filterOverlay'] as const;

/**
 * The box every styleable entity paints — the keys a block and a column spell identically. Spread
 * into each entity's own patch schema rather than nested, so the model sends one flat object.
 */
/**
 * The three contour styles the host has (`ELineStyle`), as an enum rather than the free string this
 * was.
 *
 * `entity-core` stores the field as `z.string()` on purpose — it is framework-free and keeps host
 * enum literals as the literals they are persisted as. That is right for the STORE and wrong for the
 * model-facing surface: an unconstrained string meant `"dotted-line"` or `"double"` validated here,
 * validated in the entity, reached CSS, and rendered NO border — a change that reports success and
 * does nothing. The enum turns it into a named rejection the model can correct.
 */
const borderStyleSchema = z.enum(LINE_STYLES);

/**
 * Keys the box surface spelled differently before v0 renamed what they write. Not declared in the
 * shape — see `IGNORED_BLOCK_KEYS` — so a model still sending the old spelling is told the new one
 * instead of having the key stripped in silence.
 */
export const RENAMED_BOX_KEYS = {
    isColorOverflow: 'renamed — send "isOverlay"',
    colorOverflow: 'renamed — send "overlayColor"',
    colorOverflowOpacity: 'renamed — send "overlayOpacity"',
} as const satisfies Readonly<Record<string, string>>;

export const boxPatchShape = {
    backgroundColor: z.string().optional().describe(`The background colour.${FOLLOWS_THEME}`),

    paddingTop: z.number().optional().describe('Inner top padding, px.'),
    paddingRight: z.number().optional().describe('Inner right padding, px.'),
    paddingBottom: z.number().optional().describe('Inner bottom padding, px.'),
    paddingLeft: z.number().optional().describe('Inner left padding, px.'),

    isBorder: z.boolean().optional().describe('Draw a contour at all. Without it the sides are not read.'),
    borderWidth: z.number().optional().describe('Contour width for all four sides, px.'),
    borderStyle: borderStyleSchema.optional().describe('Contour style for all four sides.'),
    borderColor: z.string().optional().describe(`Contour colour for all four sides.${LITERAL_ONLY}`),

    // Per-side overrides. Each wins over its `border*` shorthand for that side only (see
    // `foldBoxPatch`: `patch[\`border${side}Width\`] ?? patch.borderWidth`), so a uniform contour with
    // one different edge is `borderWidth` plus the one side — there is no need to spell out all four.
    // A side is only painted while `isBorder` is true.
    borderTopWidth: z
        .number()
        .optional()
        .describe('Top-edge contour width in px. Overrides borderWidth for this side.'),
    borderTopStyle: borderStyleSchema
        .optional()
        .describe('Top-edge contour style. Overrides borderStyle for this side.'),
    borderTopColor: z
        .string()
        .optional()
        .describe(`Top-edge contour colour. Overrides borderColor for this side.${LITERAL_ONLY}`),
    borderRightWidth: z
        .number()
        .optional()
        .describe('Right-edge contour width in px. Overrides borderWidth for this side.'),
    borderRightStyle: borderStyleSchema
        .optional()
        .describe('Right-edge contour style. Overrides borderStyle for this side.'),
    borderRightColor: z
        .string()
        .optional()
        .describe(`Right-edge contour colour. Overrides borderColor for this side.${LITERAL_ONLY}`),
    borderBottomWidth: z
        .number()
        .optional()
        .describe('Bottom-edge contour width in px. Overrides borderWidth for this side.'),
    borderBottomStyle: borderStyleSchema
        .optional()
        .describe('Bottom-edge contour style. Overrides borderStyle for this side.'),
    borderBottomColor: z
        .string()
        .optional()
        .describe(`Bottom-edge contour colour. Overrides borderColor for this side.${LITERAL_ONLY}`),
    borderLeftWidth: z
        .number()
        .optional()
        .describe('Left-edge contour width in px. Overrides borderWidth for this side.'),
    borderLeftStyle: borderStyleSchema
        .optional()
        .describe('Left-edge contour style. Overrides borderStyle for this side.'),
    borderLeftColor: z
        .string()
        .optional()
        .describe(`Left-edge contour colour. Overrides borderColor for this side.${LITERAL_ONLY}`),

    isOverlay: z.boolean().optional().describe('Tint the background image with a flat colour.'),
    overlayColor: z.string().optional().describe(`The tint colour.${LITERAL_ONLY}`),
    overlayOpacity: z.number().optional().describe('Tint opacity, 0..1.'),

    isFilterOverlay: z.boolean().optional().describe('Apply a CSS filter over the background.'),
    filterOverlay: z
        .string()
        .optional()
        .describe('The filter, as the CSS literal it is stored as — e.g. "blur(3px)" | "grayscale(100%)".'),
} as const;

/**
 * The scroll effect's two keys, shared by the block and the column.
 *
 * The DECLARATION is what is shared, not the fold: the block's effect is per-breakpoint and lives
 * under `background`, the column's is flat and top-level. That is also why this sits beside
 * `boxPatchShape` instead of inside it — each schema spreads it deliberately.
 *
 * Shared at all because it drifted: the value was tightened to an enum on the block and the column's
 * twin was left a `z.string()`, which is invisible until a typo reaches the page. Both entities store
 * it as a plain string — `@sites/entity-core` is framework-free and keeps the host's `EScrollEffect`
 * as a literal — so this schema is the only place a wrong name can be refused.
 */
export const scrollEffectShape = {
    isScrollEffect: z.boolean().optional().describe('Move the background as the page scrolls.'),
    scrollEffect: z
        .enum(SCROLL_EFFECTS)
        .optional()
        .describe('How the background moves: "fixed" holds it still, "parallax" drifts it.'),
} as const;

export type BoxPatch = { [K in keyof typeof boxPatchShape]?: z.infer<(typeof boxPatchShape)[K]> };

const SPACING_SEED = () => ({ value: { top: 0, right: 0, bottom: 0, left: 0 }, unit: 'px' as const });
/** What a side starts from when the stored contour has none — the one the editor's switch seeds. */
const BORDER_SIDE_SEED = createBorderSide;

/**
 * Folds the shared box keys onto a `props` tree, returning a new one. Only the groups the patch
 * actually mentions are rebuilt — an untouched group keeps its stored object, identity included,
 * which is what lets the caller diff by reference to report what changed.
 */
export function foldBoxPatch(props: PropsBag, patch: BoxPatch): PropsBag {
    const next: PropsBag = { ...props };

    if (mentions(patch, BOX_PADDING_KEYS)) {
        next['padding'] = patchEveryDevice(
            props['padding'],
            (spacing: { value: Record<string, number>; unit: string }) => ({
                ...spacing,
                value: {
                    ...spacing.value,
                    ...definedEntries({
                        top: patch.paddingTop,
                        right: patch.paddingRight,
                        bottom: patch.paddingBottom,
                        left: patch.paddingLeft,
                    }),
                },
            }),
            SPACING_SEED
        );
    }

    if (patch.isBorder !== undefined || mentions(patch, [...BORDER_SHORTHAND_KEYS, ...BORDER_SIDE_KEYS])) {
        const border = asBag(props['border']);
        // The feature carries its own version — keep whatever the stored value says rather than
        // stamping a number this package would have to know to keep in step.
        next['border'] = { ...border };
        const nextBorder = next['border'] as PropsBag;

        if (patch.isBorder !== undefined) {
            nextBorder['isEnabled'] = everyDevice(() => patch.isBorder as boolean);
        }

        for (const side of BORDER_SIDES) {
            const sideKey = side.toLowerCase();

            // A contour that was off stores no sides; switching it on seeds them the way the editor does.
            if (patch.isBorder && !nextBorder[sideKey]) {
                nextBorder[sideKey] = everyDevice(BORDER_SIDE_SEED);
            }

            const sidePatch = definedEntries({
                width: patch[`border${side}Width`] ?? patch.borderWidth,
                style: patch[`border${side}Style`] ?? patch.borderStyle,
                color: patch[`border${side}Color`] ?? patch.borderColor,
            });

            if (Object.keys(sidePatch).length) {
                nextBorder[sideKey] = patchEveryDevice(
                    border[sideKey],
                    (stored: object) => ({ ...stored, ...sidePatch }),
                    BORDER_SIDE_SEED
                );
            }
        }
    }

    if (patch.backgroundColor !== undefined || mentions(patch, [...OVERLAY_KEYS, ...FILTER_KEYS])) {
        const background = asBag(props['background']);
        next['background'] = { ...background };
        const nextBackground = next['background'] as PropsBag;

        if (patch.backgroundColor !== undefined) {
            nextBackground['color'] = everyDevice(() => patch.backgroundColor as string);
        }

        if (mentions(patch, OVERLAY_KEYS)) {
            const overlay = asBag(background['overlay']);
            nextBackground['overlay'] = {
                // A layer that was off keeps only its switch; switching it on seeds its values.
                ...(patch.isOverlay
                    ? {
                          color: everyDevice(() => BACKGROUND_LAYER_SEEDS.overlay.color),
                          opacity: everyDevice(() => BACKGROUND_LAYER_SEEDS.overlay.opacity),
                      }
                    : {}),
                ...overlay,
                ...(patch.isOverlay !== undefined ? { isEnabled: everyDevice(() => patch.isOverlay as boolean) } : {}),
                ...(patch.overlayColor !== undefined ? { color: everyDevice(() => patch.overlayColor as string) } : {}),
                ...(patch.overlayOpacity !== undefined
                    ? { opacity: everyDevice(() => patch.overlayOpacity as number) }
                    : {}),
            };
        }

        if (mentions(patch, FILTER_KEYS)) {
            const filter = asBag(background['filter']);
            nextBackground['filter'] = {
                ...(patch.isFilterOverlay ? { value: everyDevice(() => BACKGROUND_LAYER_SEEDS.filter.value) } : {}),
                ...filter,
                ...(patch.isFilterOverlay !== undefined
                    ? { isEnabled: everyDevice(() => patch.isFilterOverlay as boolean) }
                    : {}),
                ...(patch.filterOverlay !== undefined
                    ? { value: everyDevice(() => patch.filterOverlay as string) }
                    : {}),
            };
        }
    }

    return next;
}
